<template>
    <div
        class="org-chart-wrapper"
        ref="wrapperRef"
        :class="{ fullscreen: isFullscreen }"
    >
        <div class="controls-panel">
            <div class="line-switch">
                <a-radio-group
                    v-model:value="currentLineType"
                    :options="lineTypeOptions"
                    size="small"
                    @change="onLineTypeChange"
                />
            </div>

            <div class="zoom-controls">
                <a-button-group size="small">
                    <a-button title="縮小" @click="zoomOut">
                        <MinusOutlined />
                    </a-button>
                    <a-button title="放大" @click="zoomIn">
                        <PlusOutlined />
                    </a-button>
                    <a-button title="適合畫面" @click="fitToView">
                        <CompressOutlined />
                    </a-button>
                    <a-button title="重置" @click="resetView">
                        <ReloadOutlined />
                    </a-button>
                </a-button-group>
                <span class="zoom-indicator">
                    {{ Math.round(zoomLevel * 100) }}%
                </span>
                <a-button
                    size="small"
                    class="fullscreen-btn"
                    :title="isFullscreen ? '離開全螢幕' : '全螢幕'"
                    @click="toggleFullscreen"
                >
                    <FullscreenExitOutlined v-if="isFullscreen" />
                    <FullscreenOutlined v-else />
                </a-button>
            </div>
        </div>

        <p class="hint-text">拖曳移動 · Ctrl／⌘＋滾輪縮放</p>

        <div
            ref="viewportRef"
            class="viewport"
            @pointerdown="onPointerDown"
            @pointermove="onPointerMove"
            @pointerup="onPointerUp"
            @pointercancel="onPointerUp"
            @wheel="onWheel"
        >
            <div
                ref="canvasRef"
                class="canvas"
                :style="canvasStyle"
            >
                <div v-if="organizationData" class="chart-display">
                    <OrganizationChart
                        :datasource="organizationData"
                        :pan="false"
                        :zoom="false"
                    >
                        <template #default="{ nodeData }">
                            <div class="custom-node">
                                <span
                                    v-if="Number(nodeData.students_count) > 0"
                                    class="students-badge"
                                    :title="`學員數：${nodeData.students_count}`"
                                >
                                    {{ nodeData.students_count }}
                                </span>
                                <div
                                    class="node-header"
                                    :style="getHeaderStyle(nodeData)"
                                >
                                    <div class="header-left">
                                        <span class="node-name">
                                            {{ nodeData.name }}
                                        </span>
                                        <span
                                            v-if="nodeData.assigned_at"
                                            class="node-time"
                                        >
                                            {{ formatDate(nodeData.assigned_at) }}
                                        </span>
                                    </div>
                                    <span
                                        v-if="nodeData.rank_name"
                                        class="node-rank"
                                        :style="getRankBadgeStyle(nodeData)"
                                    >
                                        {{ nodeData.rank_name }}
                                    </span>
                                </div>
                                <div class="node-body">
                                    <div class="node-field-row">
                                        <div
                                            v-if="nodeData.referral_name"
                                            class="node-field"
                                        >
                                            <span class="field-label">
                                                推薦人：
                                            </span>
                                            <span class="field-value">
                                                {{ nodeData.referral_name }}
                                            </span>
                                        </div>
                                        <div
                                            v-if="nodeData.mentor_name"
                                            class="node-field"
                                        >
                                            <span class="field-label">
                                                輔導人：
                                            </span>
                                            <span class="field-value">
                                                {{ nodeData.mentor_name }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </OrganizationChart>
                </div>
                <div v-else class="no-data-message">
                    <p>沒有組織數據</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import OrganizationChart from 'vue3-organization-chart';
import {
    PlusOutlined,
    MinusOutlined,
    ReloadOutlined,
    CompressOutlined,
    FullscreenOutlined,
    FullscreenExitOutlined
} from '@ant-design/icons-vue';
import 'vue3-organization-chart/dist/orgchart.css';

const MIN_ZOOM = 0.2;
const MAX_ZOOM = 2.5;
const ZOOM_STEP = 0.1;

const props = defineProps({
    data: {
        type: Array,
        default: () => []
    },
    lineType: {
        type: String,
        default: 'referral'
    }
});

const emit = defineEmits(['lineTypeChange']);

const zoomLevel = ref(0.85);
const panX = ref(0);
const panY = ref(0);
const wrapperRef = ref(null);
const viewportRef = ref(null);
const canvasRef = ref(null);
const isFullscreen = ref(false);
const currentLineType = ref(props.lineType);

const dragging = ref(false);
const dragStart = ref({ x: 0, y: 0, panX: 0, panY: 0 });

const lineTypeOptions = [
    { label: '推薦線', value: 'referral' },
    { label: '輔導線', value: 'mentor' }
];

const canvasStyle = computed(() => ({
    transform: `translate(${panX.value}px, ${panY.value}px) scale(${zoomLevel.value})`
}));

const organizationData = computed(() => {
    if (!props.data || props.data.length === 0) return null;
    return props.data[0];
});

watch(
    () => props.lineType,
    (newVal) => {
        currentLineType.value = newVal;
    }
);

watch(
    () => props.data,
    async () => {
        await nextTick();
        fitToView();
    },
    { deep: true }
);

const onLineTypeChange = () => {
    emit('lineTypeChange', currentLineType.value);
};

const clampZoom = (value) =>
    Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(value * 100) / 100));

const zoomIn = () => {
    zoomLevel.value = clampZoom(zoomLevel.value + ZOOM_STEP);
};

const zoomOut = () => {
    zoomLevel.value = clampZoom(zoomLevel.value - ZOOM_STEP);
};

const resetView = () => {
    zoomLevel.value = 0.85;
    panX.value = 0;
    panY.value = 0;
};

const fitToView = async () => {
    await nextTick();
    const viewport = viewportRef.value;
    const canvas = canvasRef.value;
    if (!viewport || !canvas) {
        resetView();
        return;
    }

    const prevZoom = zoomLevel.value;
    const prevPanX = panX.value;
    const prevPanY = panY.value;
    zoomLevel.value = 1;
    panX.value = 0;
    panY.value = 0;
    await nextTick();

    const chartEl =
        canvas.querySelector('.chartOrgchart') ||
        canvas.querySelector('.chart-display') ||
        canvas;
    const contentW = Math.max(chartEl.scrollWidth, chartEl.offsetWidth, 1);
    const contentH = Math.max(chartEl.scrollHeight, chartEl.offsetHeight, 1);
    const viewW = viewport.clientWidth;
    const viewH = viewport.clientHeight;

    if (!viewW || !viewH) {
        zoomLevel.value = prevZoom;
        panX.value = prevPanX;
        panY.value = prevPanY;
        return;
    }

    // 內容尚未完成排版時延後重算，避免量到過小尺寸而放到 250%
    if (contentW < 180 || contentH < 120) {
        setTimeout(() => {
            fitToView();
        }, 120);
        return;
    }

    const scale = clampZoom(
        Math.min(viewW / contentW, viewH / contentH, 1) * 0.92
    );
    zoomLevel.value = scale;
    panX.value = (viewW - contentW * scale) / 2;
    panY.value = Math.max(16, (viewH - contentH * scale) / 2);
};

defineExpose({
    fitToView,
    resetView
});

const onWheel = (event) => {
    if (!(event.ctrlKey || event.metaKey)) return;
    event.preventDefault();

    const viewport = viewportRef.value;
    if (!viewport) return;

    const rect = viewport.getBoundingClientRect();
    const mx = event.clientX - rect.left;
    const my = event.clientY - rect.top;
    const oldZoom = zoomLevel.value;
    const delta = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    const newZoom = clampZoom(oldZoom + delta);
    if (newZoom === oldZoom) return;

    // 以游標為中心縮放
    const worldX = (mx - panX.value) / oldZoom;
    const worldY = (my - panY.value) / oldZoom;
    zoomLevel.value = newZoom;
    panX.value = mx - worldX * newZoom;
    panY.value = my - worldY * newZoom;
};

const onPointerDown = (event) => {
    if (event.button !== 0) return;
    dragging.value = true;
    dragStart.value = {
        x: event.clientX,
        y: event.clientY,
        panX: panX.value,
        panY: panY.value
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
};

const onPointerMove = (event) => {
    if (!dragging.value) return;
    panX.value = dragStart.value.panX + (event.clientX - dragStart.value.x);
    panY.value = dragStart.value.panY + (event.clientY - dragStart.value.y);
};

const onPointerUp = (event) => {
    dragging.value = false;
    event.currentTarget?.releasePointerCapture?.(event.pointerId);
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const isColorLight = (color) => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 180;
};

const getHeaderStyle = (nodeData) => {
    if (nodeData.rank_color) {
        const light = isColorLight(nodeData.rank_color);
        return {
            background: nodeData.rank_color,
            color: light ? '#2a2a3a' : '#ffffff'
        };
    }
    return {
        background: 'linear-gradient(45deg, #5b7cfa, #3d5afe)',
        color: '#ffffff'
    };
};

const getRankBadgeStyle = (nodeData) => {
    if (nodeData.rank_color) {
        const light = isColorLight(nodeData.rank_color);
        return {
            background: light
                ? 'rgba(42, 42, 58, 0.2)'
                : 'rgba(255, 255, 255, 0.3)',
            color: light ? '#2a2a3a' : '#ffffff'
        };
    }
    return {
        background: 'rgba(255, 255, 255, 0.3)',
        color: '#ffffff'
    };
};

const requestFs = (el) => {
    if (el.requestFullscreen) return el.requestFullscreen();
    if (el.webkitRequestFullscreen) return el.webkitRequestFullscreen();
    if (el.mozRequestFullScreen) return el.mozRequestFullScreen();
    if (el.msRequestFullscreen) return el.msRequestFullscreen();
};

const exitFs = () => {
    const d = document;
    if (d.exitFullscreen) return d.exitFullscreen();
    if (d.webkitExitFullscreen) return d.webkitExitFullscreen();
    if (d.mozCancelFullScreen) return d.mozCancelFullScreen();
    if (d.msExitFullscreen) return d.msExitFullscreen();
};

const syncFullscreenState = () => {
    isFullscreen.value = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
    );
    if (isFullscreen.value) {
        nextTick(() => fitToView());
    }
};

const toggleFullscreen = async () => {
    try {
        if (!isFullscreen.value) {
            await requestFs(wrapperRef.value);
        } else {
            await exitFs();
        }
    } catch {
        syncFullscreenState();
    }
};

onMounted(() => {
    document.addEventListener('fullscreenchange', syncFullscreenState);
    document.addEventListener('webkitfullscreenchange', syncFullscreenState);
    nextTick(() => fitToView());
});

onBeforeUnmount(() => {
    document.removeEventListener('fullscreenchange', syncFullscreenState);
    document.removeEventListener('webkitfullscreenchange', syncFullscreenState);
});
</script>

<style scoped>
.org-chart-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 520px;
    background: #fafafa;
    border: 1px solid #ececf2;
    border-radius: 10px;
    overflow: hidden;
}

.controls-panel {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 20;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    justify-content: flex-end;
}

.line-switch,
.zoom-controls {
    display: flex;
    gap: 8px;
    align-items: center;
    background: rgba(255, 255, 255, 0.96);
    padding: 6px 10px;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}

.zoom-indicator {
    min-width: 44px;
    font-size: 12px;
    color: #666;
    font-variant-numeric: tabular-nums;
    text-align: center;
}

.hint-text {
    position: absolute;
    top: 14px;
    left: 14px;
    z-index: 15;
    margin: 0;
    padding: 4px 8px;
    font-size: 12px;
    color: #888;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 6px;
    pointer-events: none;
}

.viewport {
    flex: 1;
    min-height: 0;
    cursor: grab;
    overflow: hidden;
    touch-action: none;
    user-select: none;
}

.viewport:active {
    cursor: grabbing;
}

.canvas {
    transform-origin: 0 0;
    will-change: transform;
    width: max-content;
    min-width: 100%;
    padding: 56px 24px 32px;
}

.chart-display {
    width: max-content;
    min-height: 360px;
}

.no-data-message {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 320px;
    color: #999;
}

:deep(.chartOrgchart) {
    background: transparent !important;
}

:deep(.chartNode) {
    background: #ffffff;
    border: 2px solid #5b7cfa;
    border-radius: 10px;
    min-width: 220px;
    max-width: 260px;
    box-shadow: 0 2px 10px rgba(60, 80, 180, 0.12);
    overflow: visible;
}

:deep(.chartNode:hover) {
    border-color: #3d5afe;
    box-shadow: 0 4px 16px rgba(61, 90, 254, 0.2);
}

:deep(.chartLines svg) {
    stroke: #7b8cff;
    stroke-width: 2;
}

.custom-node {
    position: relative;
    min-width: 180px;
    max-width: 220px;
}

.node-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 6px 6px 0 0;
}

.header-left {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
}

.node-name {
    font-size: 14px;
    font-weight: 700;
    color: inherit;
    word-break: break-word;
}

.node-time {
    font-size: 10px;
    color: inherit;
    opacity: 0.85;
}

.node-rank {
    flex-shrink: 0;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
}

.node-body {
    padding: 8px 12px 10px;
}

.node-field-row {
    display: flex;
    justify-content: space-between;
    gap: 8px;
}

.node-field {
    display: flex;
    font-size: 11px;
}

.field-label {
    color: #999;
    white-space: nowrap;
}

.field-value {
    color: #555;
    word-break: break-all;
}

.students-badge {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    border: 2px solid #fff;
    border-radius: 9999px;
    background: #3d5afe;
    color: #fff;
    font-size: 11px;
    box-shadow: 0 2px 8px rgba(61, 90, 254, 0.35);
}

.org-chart-wrapper.fullscreen {
    position: fixed;
    inset: 0;
    z-index: 9999;
    border-radius: 0;
    min-height: 100vh;
    background: #fff;
}

.fullscreen-btn {
    margin-left: 2px;
}
</style>
